// API 에러 정규화 — 고객 화면에 날것의 기술 정보가 새지 않게 하는 단일 통로.
//
// 배경: 각 API 클라이언트가 응답 실패 시 `HTTP 404: {"error":"..."}` 처럼 상태코드와
// 응답 본문(JSON 원문)을 통짜로 묶어 throw 했고, 화면들은 그 error.message 를 그대로
// 출력했다. 그래서 "데이터를 불러오지 못했습니다. HTTP 404: {"error":"매장이 지정되지
// 않았습니다"}" 같은 개발자용 문자열이 고객에게 노출됐다.
//
// 이 모듈이 모든 실패 응답을 받아 한 곳에서 사람이 읽을 메시지로 바꾼다:
//   - 백엔드가 의도적으로 보낸 의미 있는 한국어 안내(예: "이미 등록된 번호입니다")는 그대로 노출.
//   - 내부 상태 누설("매장이 지정되지 않았습니다", tenant/HTTP 류)·빈 본문·과도하게 긴 본문은
//     상태코드에 맞는 친화 문구로 대체.
//   - HTTP 상태코드·중괄호 JSON 원문은 message 에 절대 싣지 않는다(디버깅용으로 .status/.serverMessage 에 보존).
//   - «기술 원문처럼 보이는 글» 판정은 서버·관리자 콘솔과 같은 규칙(shared/user-facing-error.ts)을 쓴다
//     — DB 오류(Failed query…)·영어 라이브러리 메시지·한글 없는 문장은 상태코드별 안내로 바뀐다.

import { looksTechnicalMessage, toUserFacingError, toUserFacingText } from "../../shared/user-facing-error";

/** 실패 응답을 표현하는 에러. message 는 항상 고객에게 보여도 되는 문구다. */
export class ApiError extends Error {
  /** HTTP 상태코드. 화면 분기(예: 401 → 로그인 유도)에 쓸 수 있다. */
  readonly status: number;
  /** 백엔드가 보낸 원본 메시지(있으면). 콘솔·로깅용이지 화면 출력용이 아니다. */
  readonly serverMessage: string;
  /** 파싱된 응답 본문(JSON 이면). inventory 모달처럼 payload.error 를 직접 쓰던 호출부 호환용. */
  readonly payload: unknown;

  constructor(message: string, status: number, serverMessage: string, payload: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.serverMessage = serverMessage;
    this.payload = payload;
  }
}

// 고객에게 보이면 안 되는 "내부 상태 누설" 신호. 걸리면 친화 문구로 대체한다.
const INTERNAL_LEAK = [
  /매장이 지정되지\s*않았습니다/, // 테넌트(매장) 미해석 — 세션/매핑 문제, 고객이 알 필요 없음
  /tenant/i,
  /unauthorized|forbidden/i,
  /^\s*HTTP\s*\d/i, // 이미 "HTTP 404…" 로 조립된 옛 메시지
  /^\s*[{[]/, // JSON 원문이 그대로 들어온 경우
  /^[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+$/, // ACTIVATED_AT_REQUIRED 같은 내부 오류 코드
  /\bError:/,
  /at\s+\w+.*:\d+:\d+/, // 스택 트레이스 흔적
];

// 상태코드별 기본 친화 문구.
function fallbackByStatus(status: number): string {
  if (status === 401 || status === 403) {
    return "로그인이 만료됐거나 권한이 없어요. 다시 로그인해 주세요.";
  }
  if (status === 404) {
    return "정보를 불러오지 못했어요. 잠시 후 다시 시도해 주세요.";
  }
  if (status >= 500) {
    return "일시적인 오류가 발생했어요. 잠시 후 다시 시도해 주세요.";
  }
  return "요청을 처리하지 못했어요. 잠시 후 다시 시도해 주세요.";
}

/**
 * 백엔드 메시지가 "고객에게 보여도 되는 안내"인지 판단해, 그렇지 않으면 친화 문구로 바꾼다.
 * 의미 있는 짧은 한국어 안내만 통과시키고, 누설·빈값·장문은 막는다.
 */
function toFriendly(status: number, serverMessage: string): string {
  const msg = serverMessage.trim();
  const looksInternal =
    !msg ||
    msg.length > 120 || // 안내 문구라기엔 너무 길면 원문(스택/덤프)일 가능성
    INTERNAL_LEAK.some((re) => re.test(msg)) ||
    looksTechnicalMessage(msg); // DB·라이브러리 원문, 한글 없는 문장(2026-10-15)
  return looksInternal ? fallbackByStatus(status) : msg;
}

/**
 * 화면(토스트·인라인 오류)에 띄울 문구를 예외에서 고른다 — `e instanceof Error ? e.message : …` 대신 쓴다.
 * ApiError 는 이미 걸러진 문구라 그대로, 그 밖의 예외(TypeError·JSON 파싱 실패·Supabase 원문 등)는
 * 사람 말이면 그대로, 기술 원문이면 fallback 으로 바꾼다.
 */
export function friendlyErrorMessage(
  e: unknown,
  fallback = "요청을 처리하지 못했어요. 잠시 후 다시 시도해 주세요.",
): string {
  if (e instanceof ApiError) return e.message;
  return toUserFacingError(e, fallback);
}

/**
 * 서버가 DB 에 적어 둔 실패 사유(failureReason 등)를 화면에 그릴 때 거친다.
 * 2026-10-15 이전에 원문(`Failed query: …`)으로 저장된 옛 기록도 여기서 안내 문장으로 보인다.
 */
export function friendlyStoredReason(reason: string | null | undefined, fallback: string): string {
  return toUserFacingError(reason ?? "", fallback);
}

/**
 * **응답이 아예 오지 못한 실패**(타임아웃·네트워크 단절·호출부 취소)를 사람 말로 바꾼다.
 * apiError 는 Response 가 있어야 하는데, fetch 자체가 throw 하는 이 경로엔 Response 가 없어
 * 그동안 브라우저 원문이 화면까지 새어 나갔다("signal is aborted without reason" — 2026-07-28
 * 구두 관리 화면. DB 연결 고갈로 저장이 15초를 넘겨 클라이언트가 스스로 끊은 상황이었다).
 *
 * 세 갈래로 나뉜다:
 *   ① 호출부가 끊음(react-query 쿼리 취소·화면 이탈) → **에러가 아니다.** 원본을 그대로 올려보내
 *      라이브러리가 "취소"로 처리하게 둔다. 여기서 문구를 만들면 멀쩡한 화면에 경고가 뜬다.
 *   ② 우리 타임아웃이 끊음 → 저장류는 **결과를 모른다**는 사실까지 알려야 한다. 서버는 계속
 *      처리 중일 수 있어 "실패했다"고 단정하면 사용자가 같은 저장을 두 번 하게 된다.
 *   ③ 그 밖(네트워크 단절·DNS·CORS) → 연결 문제로 안내.
 */
export function toNetworkError(
  e: unknown,
  opts: { timedOut: boolean; method: string; timeoutMs: number },
): unknown {
  const isAbort = e instanceof Error && (e.name === "AbortError" || e.name === "TimeoutError");
  if (isAbort && !opts.timedOut) return e; // ① 호출부 취소 — 손대지 않는다
  const detail = e instanceof Error ? e.message : String(e);
  if (isAbort) {
    const seconds = Math.round(opts.timeoutMs / 1000);
    const isWrite = !["GET", "HEAD"].includes(opts.method);
    return new ApiError(
      isWrite
        ? `서버 응답이 ${seconds}초를 넘겨 요청을 멈췄어요. 저장됐는지 알 수 없으니 새로고침해 확인한 뒤 다시 시도해 주세요.`
        : `서버 응답이 ${seconds}초를 넘겨 요청을 멈췄어요. 잠시 후 다시 시도해 주세요.`,
      408,
      detail,
      null,
    );
  }
  return new ApiError("서버에 연결하지 못했어요. 네트워크를 확인하고 다시 시도해 주세요.", 0, detail, null);
}

/**
 * 실패한 Response 를 ApiError 로 변환한다. 응답 본문을 한 번만 읽으므로,
 * 호출부는 `if (!res.ok) throw await apiError(res);` 형태로 쓴다.
 */
export async function apiError(res: Response): Promise<ApiError> {
  const raw = await res.text().catch(() => "");
  // 백엔드가 의도적으로 보낸 구조화 메시지({error}/{message})만 화면 후보로 신뢰한다.
  // 비JSON 응답(게이트웨이 HTML·프록시 영문 등 인프라 누설)은 serverMessage 를 비워
  // 무조건 친화 문구로 덮는다.
  let serverMessage = "";
  let payload: unknown = null;
  try {
    const parsed = JSON.parse(raw) as { error?: unknown; message?: unknown };
    payload = parsed;
    // error 는 기계용 코드인 응답이 많다. 함께 온 message(사람용)를 우선한다.
    if (typeof parsed?.message === "string") serverMessage = parsed.message;
    else if (typeof parsed?.error === "string") serverMessage = parsed.error;
  } catch {
    // 비JSON → serverMessage 는 빈값 유지(친화 문구로 폴백).
  }
  return new ApiError(toFriendly(res.status, serverMessage), res.status, serverMessage, payload);
}

/**
 * 서버가 보낸 오류 본문(`body.error`)을 화면용으로 — 공개 화면(입력폼·페이백·시세표 링크 등)처럼
 * apiError 를 안 거치는 생 fetch 경로에서 쓴다. 일부러 쓴 한국어 안내만 통과, 원문은 fallback.
 */
export function friendlyBodyError(bodyError: unknown, fallback: string): string {
  return toUserFacingText(typeof bodyError === "string" ? bodyError : "", fallback);
}

/**
 * 로그인 없는 공개 화면용 fetch — 응답이 아예 안 오면(인터넷 끊김) 브라우저 원문
 * `TypeError: Failed to fetch`·`Load failed` 대신 사람 말 ApiError 를 던진다. 호출부 취소는 그대로.
 */
export async function publicFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  try {
    return await fetch(input, init);
  } catch (e) {
    if (e instanceof Error && (e.name === "AbortError" || e.name === "TimeoutError")) throw e;
    const detail = e instanceof Error ? e.message : String(e);
    throw new ApiError(
      "서버에 연결하지 못했어요. 인터넷 연결을 확인하고 다시 시도해 주세요.",
      0,
      detail,
      null,
    );
  }
}
